import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  Users, 
  Printer, 
  Car, 
  GraduationCap,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Phone,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Image Carousel Component for subcategories
const ImageCarousel = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <div className="relative w-full h-full overflow-hidden group">
      {images.map((img, idx) => (
        <img
          key={idx}
          src={img}
          alt={`${title} ${idx + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            idx === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex ? 'bg-white w-4' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const iconMap = {
  Users: Users,
  Printer: Printer,
  Car: Car,
  GraduationCap: GraduationCap,
};

const slugMap = {
  'insan-kaynaklari': 'İnsan Kaynakları Hizmetleri',
  'dijital-baski': 'Dijital Baskı Çözümleri',
  'arac-kiralama': 'Araç Kiralama Hizmetleri',
  'egitim-danismanlik': 'Eğitim ve Danışmanlık',
};

const subcategoryImageMap = {
  // İnsan Kaynakları Hizmetleri
  'Personel Tedariki': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/5ckp734o_IMG-20260325-WA0039.jpg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/2nswmgjr_IMG-20260325-WA0040.jpg'
  ],
  'Mavi Yaka Personel Tedariki': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/5ckp734o_IMG-20260325-WA0039.jpg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/2nswmgjr_IMG-20260325-WA0040.jpg'
  ],
  'Bordrolama Hizmetleri': [
    'https://images.pexels.com/photos/6863250/pexels-photo-6863250.jpeg?auto=compress&cs=tinysrgb&w=800',
    'https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg?auto=compress&cs=tinysrgb&w=800'
  ],
  'Seçme ve Yerleştirme': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/07ofoj27_1000200539.jpg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/iikpjjwk_1000200533.jpg'
  ],
  'Fabrika Personeli': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/qo9g2lfb_1000200542.jpg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/zeg4i1dd_1000200541.jpg'
  ],
  'Fabrika ve Üretim Personeli': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/qo9g2lfb_1000200542.jpg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/zeg4i1dd_1000200541.jpg'
  ],
  'Plaza Personeli': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/0fe8bmgx_1000200555.jpg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/0y43hw7b_1000200556.jpg'
  ],
  'Plaza ve İş Merkezi Personeli': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/0fe8bmgx_1000200555.jpg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/0y43hw7b_1000200556.jpg'
  ],
  'Sağlık Personeli': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/hg5wwoma_1000200591.jpg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/i9b441gq_1000200592.jpg'
  ],
  'Sağlık Kurumları Personeli': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/hg5wwoma_1000200591.jpg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/i9b441gq_1000200592.jpg'
  ],
  'Teknik Personel': [
    'https://images.pexels.com/photos/4487446/pexels-photo-4487446.jpeg?auto=compress&cs=tinysrgb&w=800',
    'https://images.pexels.com/photos/4487419/pexels-photo-4487419.jpeg?auto=compress&cs=tinysrgb&w=800'
  ],
  // Dijital Baskı Çözümleri
  'Kurumsal Kimlik': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/yxzo4x4m_WhatsApp%20Image%202026-03-26%20at%2013.04.20.jpeg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/aqyta05v_WhatsApp%20Image%202026-03-26%20at%2013.04.21.jpeg'
  ],
  'Promosyon Ürünleri': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/jihu1vf1_WhatsApp%20Image%202026-03-26%20at%2013.06.21%20%281%29.jpeg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/tiqx5dmu_WhatsApp%20Image%202026-03-26%20at%2013.06.21.jpeg'
  ],
  'Afiş ve Broşür': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/7zlubxxt_WhatsApp%20Image%202026-03-26%20at%2009.53.40%20%281%29.jpeg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/1pic7hla_WhatsApp%20Image%202026-03-26%20at%2009.53.40.jpeg'
  ],
  // Araç Kiralama Hizmetleri
  'Günlük Kiralama': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/pha94wbv_4.jpg.jpeg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/kt7v4n5x_5.jpg.jpeg'
  ],
  'Uzun Dönem Kiralama': [
    'https://images.unsplash.com/photo-1763180785425-46ea06a0fc50?w=800',
    'https://images.pexels.com/photos/36729874/pexels-photo-36729874.jpeg?auto=compress&cs=tinysrgb&w=800'
  ],
  'Şoförlü Araç': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/08on8tar_1.jpg.jpeg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/kk2xtvmv_6.jpg.jpeg'
  ],
  // Eğitim ve Danışmanlık
  'Satış Eğitimleri': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/2uvja40v_WhatsApp%20Image%202026-03-26%20at%2010.23.52%20%281%29.jpeg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/yzcp550h_WhatsApp%20Image%202026-03-26%20at%2010.23.52.jpeg'
  ],
  'Satış ve Pazarlama Eğitimleri': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/2uvja40v_WhatsApp%20Image%202026-03-26%20at%2010.23.52%20%281%29.jpeg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/yzcp550h_WhatsApp%20Image%202026-03-26%20at%2010.23.52.jpeg'
  ],
  'Pazarlama Danışmanlığı': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/unuqg66b_WhatsApp%20Image%202026-03-26%20at%2012.59.06%20%281%29.jpeg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/dpnvbt2v_WhatsApp%20Image%202026-03-26%20at%2012.59.06.jpeg'
  ],
  'Kurumsal Gelişim': [
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/4sz1a3jx_WhatsApp%20Image%202026-03-26%20at%2013.09.57%20%281%29.jpeg',
    'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/r0zntvtl_WhatsApp%20Image%202026-03-26%20at%2013.09.57.jpeg'
  ],
  'Liderlik ve Yöneticilik': [
    'https://images.unsplash.com/photo-1765438864227-288900d09d26?w=800',
    'https://images.unsplash.com/photo-1758691736084-4ef3e6f6a2cf?w=800'
  ],
  'İş Sağlığı ve Güvenliği': [
    'https://images.pexels.com/photos/8961251/pexels-photo-8961251.jpeg?auto=compress&cs=tinysrgb&w=800',
    'https://images.pexels.com/photos/8961065/pexels-photo-8961065.jpeg?auto=compress&cs=tinysrgb&w=800'
  ],
  'İnsan Kaynakları Danışmanlığı': [
    'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=800',
    'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800'
  ],
  'Operasyonel Verimlilik': [
    'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=800',
    'https://images.pexels.com/photos/6476808/pexels-photo-6476808.jpeg?auto=compress&cs=tinysrgb&w=800'
  ]
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/categories`);
        const categoryTitle = slugMap[slug];
        const found = response.data.find(cat => cat.title === categoryTitle);
        if (found) {
          // Merge subcategory images
          if (found.subcategories) {
            found.subcategories = found.subcategories.map(sub => ({
              ...sub,
              images: sub.images || subcategoryImageMap[sub.title] || []
            }));
          }
          setCategory(found);
        }
      } catch (error) {
        console.error('Error fetching category:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategory();
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#005CB2] animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!category) {
    return (
      <Layout>
        <div className="eta-container py-20 text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Kategori Bulunamadı</h1>
          <Link to="/hizmetler">
            <Button className="bg-[#005CB2] hover:bg-[#004a91]">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Hizmetlere Dön
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const IconComponent = iconMap[category.icon] || Users;

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-[#005CB2] py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-48 md:w-96 h-48 md:h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 md:w-64 h-32 md:h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>

        <div className="eta-container relative z-10 px-4 md:px-6">
          <Link 
            to="/hizmetler" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Tüm Hizmetler
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center">
              <IconComponent className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {category.title}
            </h1>
          </div>

          <p className="text-lg md:text-xl text-white/80 max-w-3xl leading-relaxed">
            {category.description}
          </p>
        </div>
      </section>

      {/* Main Image */}
      <section className="py-12 bg-white">
        <div className="eta-container px-4 md:px-6">
          <div className="rounded-2xl overflow-hidden shadow-lg max-w-4xl mx-auto">
            <img 
              src={category.images?.[0] || category.image_url}
              alt={category.title}
              className="w-full h-[300px] md:h-[400px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Subcategories */}
      {category.subcategories && category.subcategories.length > 0 && (
        <section className="py-12 bg-slate-50">
          <div className="eta-container px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Alt Hizmetlerimiz
            </h2>

            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {category.subcategories.map((sub, index) => (
                <Card key={sub.id} className="overflow-hidden border-slate-200 hover:shadow-lg transition-shadow">
                  {sub.images && sub.images.length > 0 && (
                    <div className="aspect-video relative">
                      <ImageCarousel images={sub.images} title={sub.title} />
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-3 text-lg">
                      <CheckCircle2 className="w-5 h-5 text-[#005CB2]" />
                      {sub.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {sub.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="eta-container px-4 md:px-6">
          <Card className="bg-gradient-to-r from-[#005CB2] to-[#3D94F6] border-0 overflow-hidden">
            <CardContent className="p-8 md:p-12 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Bu Hizmetten Faydalanmak İster misiniz?
              </h3>
              <p className="text-white/80 mb-8 max-w-2xl mx-auto">
                Profesyonel ekibimiz sizinle iletişime geçmek için hazır. Hemen bizimle iletişime geçin.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/iletisim">
                  <Button size="lg" className="bg-white text-[#005CB2] hover:bg-slate-100 gap-2 w-full sm:w-auto">
                    <Phone className="w-4 h-4" />
                    İletişime Geç
                  </Button>
                </Link>
                <Link to="/personel-talep">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 gap-2 w-full sm:w-auto">
                    Personel Talep Formu
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default ServiceDetailPage;
